"use client";

import { useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useAuth } from "@/lib/auth-context";
import { LogOut, Menu, User, Settings, X } from "lucide-react";

export default function HomePage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout, isLoading, isAdmin } = useAuth();
  const router = useRouter();

  const handleLogout = async () => {
    await logout();
    router.push("/");
  };

  return (
    <div className="min-h-screen bg-[#F3F6FA] text-[#101828]">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-[#F3F6FA]/95 backdrop-blur">
        <div className="border-b border-[#DCE3EA]">
          <div className="mx-auto flex h-8 max-w-[1440px] items-center justify-between px-5 text-[10px] uppercase tracking-[.15em] text-slate-500 sm:px-8 lg:px-12">
            <span className="mono">Kenya payroll infrastructure</span>
            <div className="hidden items-center gap-5 sm:flex">
              <span>2026 tax tables</span>
              <a href="#payroll-tools" className="quiet-link hover:underline">Free calculators</a>
              <Link href="/contact" className="quiet-link hover:underline">Support</Link>
            </div>
          </div>
        </div>

        <div className="border-b border-[#DCE3EA]">
          <div className="mx-auto grid h-[70px] max-w-[1440px] grid-cols-[1fr_auto] items-center px-5 sm:px-8 lg:grid-cols-[240px_1fr_auto] lg:px-12">
            <Link href="/" className="justify-self-start text-left">
              <div className="wordmark text-slate-950">SekaniPay</div>
              <div className="mono mt-1 text-[9px] tracking-[.12em] text-slate-400">PAYROLL / PEOPLE / KE</div>
            </Link>

            <nav className="hidden h-full items-stretch justify-self-start lg:flex">
              <Link href="/salary-calculator" className="flex items-center border-l border-[#DCE3EA] px-6 text-[13px] text-slate-600 hover:bg-white/60 hover:text-slate-950">Calculate</Link>
              <Link href="/paye-calculator" className="block border-b border-[#DCE3EA] py-3 text-[13px] text-slate-600" >PAYE</Link>
              <Link href="/bulk-payroll" className="flex items-center border-l border-[#DCE3EA] px-6 text-[13px] text-slate-600 hover:bg-white/60 hover:text-slate-950">Run payroll</Link>
              <Link href="/payslip-generator" className="flex items-center border-l border-[#DCE3EA] px-6 text-[13px] text-slate-600 hover:bg-white/60 hover:text-slate-950">Payslips</Link>
              <Link href="/blog" className="flex items-center border-l border-[#DCE3EA] px-6 text-[13px] text-slate-600 hover:bg-white/60 hover:text-slate-950">Resources</Link>
              <div className="border-l border-[#DCE3EA]" />
            </nav>

            <div className="flex items-center justify-self-end gap-5">
              {!isLoading && user && (
                <>
                  {isAdmin && (
                    <Link href="/admin/config" className="hidden text-[12px] font-medium text-slate-600 underline decoration-[#B8B6AE] quiet-link hover:text-slate-950 sm:block">
                      Admin
                    </Link>
                  )}
                  <span className="hidden text-[12px] text-slate-500 sm:block">{user.fullName}</span>
                  <button onClick={handleLogout} className="hidden text-[12px] font-medium text-slate-600 underline decoration-[#B8B6AE] quiet-link hover:text-slate-950 sm:block">
                    Sign out
                  </button>
                </>
              )}
              <Link href="/workspace" className="hidden ink-button border border-slate-950 bg-slate-950 px-4 py-2.5 text-[12px] font-medium text-white transition hover:border-[#2348C7] hover:bg-[#2348C7] sm:block">
                Open workspace
              </Link>
              <button
                className="lg:hidden p-2 text-slate-600 hover:text-slate-950"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="border-b border-[#DCE3EA] bg-white px-5 py-4 lg:hidden animate-fade-up">
            <div className="space-y-1">
              <Link href="/salary-calculator" className="block border-b border-[#DCE3EA] py-3 text-[13px] text-slate-600" onClick={() => setMobileMenuOpen(false)}>Calculate</Link>
              <Link href="/paye-calculator" className="block border-b border-[#DCE3EA] py-3 text-[13px] text-slate-600" onClick={() => setMobileMenuOpen(false)}>PAYE</Link>
              <Link href="/payslip-generator" className="block border-b border-[#DCE3EA] py-3 text-[13px] text-slate-600" onClick={() => setMobileMenuOpen(false)}>Payslips</Link>
              <Link href="/bulk-payroll" className="block border-b border-[#DCE3EA] py-3 text-[13px] text-slate-600" onClick={() => setMobileMenuOpen(false)}>Bulk Payroll</Link>
              <Link href="/blog" className="block border-b border-[#DCE3EA] py-3 text-[13px] text-slate-600" onClick={() => setMobileMenuOpen(false)}>Blog</Link>
              <Link href="/contact" className="block border-b border-[#DCE3EA] py-3 text-[13px] text-slate-600" onClick={() => setMobileMenuOpen(false)}>Contact</Link>
              {!isLoading && user && (
                <div className="pt-3">
                  <div className="flex items-center gap-2 py-2 text-[12px] text-slate-500">
                    <User className="h-3.5 w-3.5" />{user.fullName}
                  </div>
                  {isAdmin && (
                    <Link href="/admin/config" className="flex items-center gap-2 py-2 text-[12px] text-[#2348C7]" onClick={() => setMobileMenuOpen(false)}>
                      <Settings className="h-3.5 w-3.5" />Admin
                    </Link>
                  )}
                  <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="flex items-center gap-2 py-2 text-[12px] text-slate-600">
                    <LogOut className="h-3.5 w-3.5" />Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </header>

      <main>
        {/* Hero Section */}
        <section className="border-b border-[#DCE3EA]">
          <div className="mx-auto grid max-w-[1440px] lg:grid-cols-[minmax(0,1.05fr)_minmax(520px,.95fr)]">
            <div className="border-b border-[#DCE3EA] px-5 py-14 sm:px-8 sm:py-20 lg:border-b-0 lg:border-r lg:px-12 lg:py-24 xl:py-28">
              <div className="max-w-3xl">
                <div>
                  <h1 className="font-[var(--font-source-serif)] max-w-[760px] text-[46px] leading-[.98] tracking-[-.035em] text-slate-950 sm:text-[58px] lg:text-[66px] xl:text-[74px]">
                    Payroll calculations you can trust, for free.
                  </h1>
                  <p className="mt-7 max-w-xl text-[17px] leading-7 text-slate-600">
                    PAYE, NSSF, SHIF, Housing Levy and net pay — computed with precision. No registration, no hidden fees.
                  </p>

                  <div className="mt-9 flex flex-wrap items-center gap-x-7 gap-y-4">
                    <Link href="/salary-calculator" className="ink-button bg-[#2348C7] px-5 py-3 text-[13px] font-medium text-white hover:bg-[#18369B]">
                      Calculate a salary
                    </Link>
                    <Link href="/bulk-payroll" className="quiet-link text-[13px] font-medium text-slate-700 underline decoration-[#AAA89F] hover:text-[#2348C7]">
                      Process bulk payroll instead
                    </Link>
                  </div>

                  <div className="mt-12 grid max-w-2xl border-y border-[#DCE3EA] sm:grid-cols-3">
                    <div className="py-4 sm:border-r sm:border-[#DCE3EA] sm:pr-5">
                      <p className="mono text-[9px] uppercase tracking-[.13em] text-slate-400">Price</p>
                      <p className="mt-2 text-[13px] text-slate-700">Free forever</p>
                    </div>
                    <div className="border-t border-[#DCE3EA] py-4 sm:border-t-0 sm:border-r sm:px-5 sm:border-[#DCE3EA]">
                      <p className="mono text-[9px] uppercase tracking-[.13em] text-slate-400">Account</p>
                      <p className="mt-2 text-[13px] text-slate-700">No registration needed</p>
                    </div>
                    <div className="border-t border-[#DCE3EA] py-4 sm:border-t-0 sm:pl-5">
                      <p className="mono text-[9px] uppercase tracking-[.13em] text-slate-400">Compliance</p>
                      <p className="mt-2 text-[13px] text-slate-700">Current KRA statutory rates</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Operational preview */}
            <div className="bg-[#F3F6FA] p-5 sm:p-8 lg:p-10 xl:p-12">
              <div className="square-card border border-[#CBD5E1] bg-white">
                <div className="flex items-center justify-between border-b border-[#DCE3EA] px-5 py-4">
                  <div>
                    <p className="wordmark text-[10px] text-slate-900">Sample Calculation</p>
                    <p className="mono mt-1 text-[9px] uppercase tracking-[.1em] text-slate-400">Single employee / KSh 120,000 gross</p>
                  </div>
                  <div className="mono text-[10px] text-[#2348C7]">2026</div>
                </div>

                <div className="grid grid-cols-3 border-b border-[#DCE3EA] text-[11px]">
                  <div className="border-r border-[#DCE3EA] px-4 py-4">
                    <span className="mono block text-[9px] uppercase text-slate-400">Gross</span>
                    <strong className="mt-1 block text-base font-medium">120,000</strong>
                  </div>
                  <div className="border-r border-[#DCE3EA] px-4 py-4">
                    <span className="mono block text-[9px] uppercase text-slate-400">Deductions</span>
                    <strong className="mt-1 block text-base font-medium text-red-500">−36,489</strong>
                  </div>
                  <div className="px-4 py-4">
                    <span className="mono block text-[9px] uppercase text-slate-400">Net pay</span>
                    <strong className="mt-1 block text-base font-medium text-[#0F7B4D]">83,511</strong>
                  </div>
                </div>

                <div className="px-5 py-4">
                  <div className="mb-2 grid grid-cols-[1fr_92px] gap-3 border-b border-[#DCE3EA] pb-2 mono text-[9px] uppercase tracking-[.08em] text-slate-400">
                    <span>Deduction</span><span className="text-right">Amount</span>
                  </div>
                  <div className="space-y-0 text-[12px] text-slate-700">
                    <div className="grid grid-cols-[1fr_92px] gap-3 border-b border-[#E8E7E1] py-3"><span>PAYE</span><span className="text-right mono">24,909.35</span></div>
                    <div className="grid grid-cols-[1fr_92px] gap-3 border-b border-[#E8E7E1] py-3"><span>NSSF</span><span className="text-right mono">6,480.00</span></div>
                    <div className="grid grid-cols-[1fr_92px] gap-3 border-b border-[#E8E7E1] py-3"><span>SHIF</span><span className="text-right mono">3,300.00</span></div>
                    <div className="grid grid-cols-[1fr_92px] gap-3 py-3"><span>Housing Levy</span><span className="text-right mono">1,800.00</span></div>
                  </div>
                </div>

                <div className="grid grid-cols-2 border-t border-[#DCE3EA]">
                  <Link href="/salary-calculator" className="border-r border-[#DCE3EA] px-5 py-4 text-left text-[12px] font-medium hover:bg-[#F3F6FA]">
                    Try calculator →
                  </Link>
                  <Link href="/bulk-payroll" className="px-5 py-4 text-left text-[12px] font-medium text-[#2348C7] hover:bg-[#F3F6FA]">
                    Process team payroll →
                  </Link>
                </div>
              </div>
              <p className="mono mt-4 text-[9px] uppercase leading-4 tracking-[.1em] text-slate-400">Illustrative example · actual results depend on employee inputs and statutory rules</p>
            </div>
          </div>
        </section>

        {/* Free Tools Section */}
        <section id="payroll-tools" className="border-b border-[#DCE3EA] bg-white">
          <div className="mx-auto max-w-[1440px] px-5 py-16 sm:px-8 lg:px-12 lg:py-20">
            <div className="grid gap-10 lg:grid-cols-[260px_1fr]">
              <div>
                <h2 className="font-[var(--font-source-serif)] mt-3 text-3xl leading-tight tracking-[-.02em]">No account required for any calculation.</h2>
              </div>
              <div>
                <p className="max-w-3xl text-[16px] leading-7 text-slate-600">
                  Every calculator on SekaniPay is free to use without creating an account. Salary calculations, PAYE breakdowns, payslip generation, and bulk payroll processing — all accessible immediately.
                </p>
                <div className="mt-9 grid border-y border-[#DCE3EA] sm:grid-cols-3">
                  <div className="py-5 sm:border-r sm:border-[#DCE3EA] sm:pr-6">
                    <span className="mono text-[9px] uppercase text-slate-400">Calculator price</span>
                    <p className="mt-2 text-2xl font-medium">Free</p>
                    <p className="mt-1 text-[12px] text-slate-500">All payroll tools</p>
                  </div>
                  <div className="border-t border-[#DCE3EA] py-5 sm:border-t-0 sm:border-r sm:px-6 sm:border-[#DCE3EA]">
                    <span className="mono text-[9px] uppercase text-slate-400">Account needed</span>
                    <p className="mt-2 text-2xl font-medium">No</p>
                    <p className="mt-1 text-[12px] text-slate-500">Use immediately</p>
                  </div>
                  <div className="border-t border-[#DCE3EA] py-5 sm:border-t-0 sm:pl-6">
                    <span className="mono text-[9px] uppercase text-slate-400">Data stored</span>
                    <p className="mt-2 text-2xl font-medium">None</p>
                    <p className="mt-1 text-[12px] text-slate-500">Private by design</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Tools Catalogue */}
        <section className="bg-[#F3F6FA]">
          <div className="mx-auto max-w-[1440px] px-5 py-16 sm:px-8 lg:px-12 lg:py-24">
            <div className="grid gap-12 lg:grid-cols-[360px_1fr]">
              <div className="lg:sticky lg:top-32 lg:self-start">
                <h2 className="font-[var(--font-source-serif)] mt-4 text-4xl leading-[1.05] tracking-[-.025em]">Every Kenyan payroll calculation, one place.</h2>
                <p className="mt-5 text-[15px] leading-7 text-slate-600">
                  From a single salary check to bulk team processing — the calculators cover PAYE, NSSF, SHIF, Housing Levy and net pay with full statutory compliance.
                </p>
                <Link href="/salary-calculator" className="quiet-link mt-7 inline-block text-[13px] font-medium text-[#2348C7] underline decoration-[#2348C7]">
                  Start with salary calculator →
                </Link>
              </div>

              <div className="border-t border-[#BDBBB3]">
                <Link href="/salary-calculator" className="group grid w-full grid-cols-[88px_1fr_auto] gap-4 border-b border-[#DCE3EA] py-5 transition hover:bg-white/40">
                  <span className="mono text-[9px] uppercase tracking-[.1em] text-slate-400">CALC 01</span>
                  <div>
                    <p className="font-medium">Salary Calculator</p>
                    <p className="mt-1 text-[13px] text-slate-500">Gross to net with all Kenyan statutory deductions itemized.</p>
                  </div>
                  <span className="mono self-center text-[10px] uppercase text-[#0F7B4D] opacity-0 transition group-hover:opacity-100">Open →</span>
                </Link>

                <Link href="/paye-calculator" className="group grid w-full grid-cols-[88px_1fr_auto] gap-4 border-b border-[#DCE3EA] py-5 transition hover:bg-white/40">
                  <span className="mono text-[9px] uppercase tracking-[.1em] text-slate-400">CALC 02</span>
                  <div>
                    <p className="font-medium">PAYE Calculator</p>
                    <p className="mt-1 text-[13px] text-slate-500">Pay-As-You-Earn tax computation with band-by-band breakdown.</p>
                  </div>
                  <span className="mono self-center text-[10px] uppercase text-[#0F7B4D] opacity-0 transition group-hover:opacity-100">Open →</span>
                </Link>

                <Link href="/payslip-generator" className="group grid w-full grid-cols-[88px_1fr_auto] gap-4 border-b border-[#DCE3EA] py-5 transition hover:bg-white/40">
                  <span className="mono text-[9px] uppercase tracking-[.1em] text-slate-400">GEN 03</span>
                  <div>
                    <p className="font-medium">Payslip Generator</p>
                    <p className="mt-1 text-[13px] text-slate-500">Individual employee payslips with PDF export for records.</p>
                  </div>
                  <span className="mono self-center text-[10px] uppercase text-[#0F7B4D] opacity-0 transition group-hover:opacity-100">Open →</span>
                </Link>

                <Link href="/bulk-payroll" className="group grid w-full grid-cols-[88px_1fr_auto] gap-4 border-b border-[#DCE3EA] py-5 transition hover:bg-white/40">
                  <span className="mono text-[9px] uppercase tracking-[.1em] text-slate-400">BULK 04</span>
                  <div>
                    <p className="font-medium">Bulk Payroll</p>
                    <p className="mt-1 text-[13px] text-slate-500">Upload CSV or Excel and process entire team payroll at once.</p>
                  </div>
                  <span className="mono self-center text-[10px] uppercase text-[#0F7B4D] opacity-0 transition group-hover:opacity-100">Open →</span>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Workspace Teaser */}
        <section className="border-t border-[#DCE3EA] bg-white">
          <div className="mx-auto max-w-[1440px] px-5 py-16 sm:px-8 lg:px-12 lg:py-20">
            <div className="grid gap-10 lg:grid-cols-[260px_1fr]">
              <div>
                <h2 className="font-[var(--font-source-serif)] mt-3 text-3xl leading-tight tracking-[-.02em]">Your payroll tool becomes your people system.</h2>
              </div>
              <div>
                <p className="max-w-3xl text-[16px] leading-7 text-slate-600">
                  Keep the free calculators. When you need continuity, add a private workspace for employee records, payroll runs, leave, attendance and expenses. Registration starts only when a business wants to save people, settings and repeat work.
                </p>
                <div className="mt-9 grid border-y border-[#DCE3EA] sm:grid-cols-4">
                  <div className="py-5 sm:border-r sm:border-[#DCE3EA] sm:pr-5">
                    <span className="mono text-[9px] uppercase text-slate-400">Core HR</span>
                    <p className="mt-2 text-[13px] text-slate-700">Included</p>
                  </div>
                  <div className="border-t border-[#DCE3EA] py-5 sm:border-t-0 sm:border-r sm:px-5 sm:border-[#DCE3EA]">
                    <span className="mono text-[9px] uppercase text-slate-400">Payroll</span>
                    <p className="mt-2 text-[13px] text-slate-700">Modular</p>
                  </div>
                  <div className="border-t border-[#DCE3EA] py-5 sm:border-t-0 sm:border-r sm:px-5 sm:border-[#DCE3EA]">
                    <span className="mono text-[9px] uppercase text-slate-400">Leave</span>
                    <p className="mt-2 text-[13px] text-slate-700">Modular</p>
                  </div>
                  <div className="border-t border-[#DCE3EA] py-5 sm:border-t-0 sm:pl-5">
                    <span className="mono text-[9px] uppercase text-slate-400">Tenant</span>
                    <p className="mt-2 text-[13px] text-slate-700">yourco.sekanipay.com</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#2A354A] bg-[#172033] text-[#D7DEE8]">
        <div className="mx-auto max-w-[1440px] px-5 py-12 sm:px-8 lg:px-12">
          <div className="grid gap-10 md:grid-cols-[1fr_auto_auto]">
            <div>
              <p className="wordmark text-white">SekaniPay</p>
              <p className="mt-3 max-w-sm text-[13px] leading-6 text-[#93A1B3]">
                Free payroll tools and private business workspaces for Kenyan teams.
              </p>
            </div>
            <div>
              <p className="mono text-[9px] uppercase tracking-[.13em] text-[#93A1B3] mb-4">Product</p>
              <ul className="space-y-2.5">
                <li><Link href="/salary-calculator" className="text-[13px] text-[#D7DEE8] hover:text-white transition-colors">Salary Calculator</Link></li>
                <li><Link href="/paye-calculator" className="text-[13px] text-[#D7DEE8] hover:text-white transition-colors">PAYE Calculator</Link></li>
                <li><Link href="/bulk-payroll" className="text-[13px] text-[#D7DEE8] hover:text-white transition-colors">Bulk Payroll</Link></li>
                <li><Link href="/payslip-generator" className="text-[13px] text-[#D7DEE8] hover:text-white transition-colors">Payslip Generator</Link></li>
              </ul>
            </div>
            <div>
              <p className="mono text-[9px] uppercase tracking-[.13em] text-[#93A1B3] mb-4">Legal</p>
              <ul className="space-y-2.5">
                <li><Link href="/privacy" className="text-[13px] text-[#D7DEE8] hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link href="/terms" className="text-[13px] text-[#D7DEE8] hover:text-white transition-colors">Terms of Service</Link></li>
                <li><Link href="/contact" className="text-[13px] text-[#D7DEE8] hover:text-white transition-colors">Contact</Link></li>
              </ul>
            </div>
          </div>
          <div className="mt-10 border-t border-[#2A354A] pt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-[11px] text-[#93A1B3]">&copy; 2026 SekaniPay. Built for Kenya.</p>
            <p className="mono text-[9px] uppercase tracking-[.1em] text-[#93A1B3]">Nairobi / Kenya · 2026</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
